import React, { useState } from 'react';
import { Box, Button, Alert, CircularProgress, Typography } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { apiClient } from '../../utils/apiClient';

/**
 * ClicToPay top-up.
 *
 * ClicToPay is a redirect gateway: the server registers the order and returns a
 * hosted payment page to send the buyer to. There is deliberately no card form
 * here — card details are entered on ClicToPay's own page, so they never touch
 * this origin.
 *
 * The wallet is credited by the server after it re-queries the gateway for the
 * authoritative order status (see routes/payments.js). Coming back from the
 * redirect is never treated as proof of payment.
 */
export default function ClicToPayCheckout({ amountTND, onError }) {
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const startPayment = async () => {
    setLoading(true);
    setLocalError('');
    try {
      const res = await apiClient.post('/api/payments/clictopay/create-order', { amountTND });
      if (res?.success && res.formUrl) {
        // Hand off to the hosted payment page.
        window.location.assign(res.formUrl);
        return;
      }
      const message = res?.message || 'Could not start the payment. Please try again.';
      setLocalError(message);
      onError?.(message);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Could not reach the payment gateway.';
      setLocalError(message);
      onError?.(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {localError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setLocalError('')}>
          {localError}
        </Alert>
      )}

      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        You will be taken to ClicToPay to enter your card details securely, then
        returned here once the payment is confirmed.
      </Typography>

      <Button
        fullWidth
        size="large"
        variant="contained"
        onClick={startPayment}
        disabled={loading || !(amountTND >= 1)}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CreditCardIcon />}
        sx={{ borderRadius: '12px', fontWeight: 700, py: 1.4 }}
      >
        {loading ? 'Contacting ClicToPay…' : `Pay ${amountTND} TND with ClicToPay`}
      </Button>
    </Box>
  );
}
